import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../context/ProjectContext';
import { RefreshCw, Filter, Search } from 'lucide-react';
import EntityBadge from '../../components/EntityBadge';

interface Entity {
    id: string;
    entity_id: string;
    entity_type: string;
    title: string | null;
    spec_status: string;
    spec_blocked_reason: string | null;
    spec_version_id: string | null;
    spec_reviewed_at: string | null;
    precheck_chunk_count: number | null;
    precheck_sources_count: number | null;
    precheck_confidence: number | null;
    created_at: string | null;
}

const Entities: React.FC = () => {
    const { currentProject } = useProject();
    const navigate = useNavigate();
    const [entities, setEntities] = useState<Entity[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [retryingId, setRetryingId] = useState<string | null>(null);

    useEffect(() => {
        if (currentProject?.value) {
            setEntities([]);
            loadEntities();
        }
    }, [currentProject?.value, statusFilter]);

    const loadEntities = async () => {
        if (!currentProject?.value) return;

        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }

            const response = await fetch(
                `/api/spec-review/projects/${currentProject.value}/entities?${params}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setEntities(data.entities || []);
            }
        } catch (error) {
            console.error('Error loading entities:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRetry = async (entityId: string) => {
        if (!currentProject?.value) return;

        try {
            setRetryingId(entityId);
            const response = await fetch(
                `/api/spec-review/projects/${currentProject.value}/entities/${entityId}/spec-review/retry`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const result = await response.json();
                alert(result.message);
                await loadEntities();
            }
        } catch (error) {
            console.error('Error retrying spec review:', error);
            alert('Error retrying spec review');
        } finally {
            setRetryingId(null);
        }
    };

    const filteredEntities = entities.filter(e =>
        e.entity_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (e.title && e.title.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-white">Detected Entities</h2>
                    <p className="text-slate-400">
                        Entities extracted from ingested documents with spec review status
                    </p>
                </div>
                <button
                    onClick={loadEntities}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {/* Filters */}
            <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
                <div className="flex gap-4 items-center">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search entities..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-slate-400" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                        >
                            <option value="all">All Status</option>
                            <option value="READY">Ready</option>
                            <option value="QUEUED">Queued</option>
                            <option value="BLOCKED">Blocked</option>
                            <option value="FAILED">Failed</option>
                            <option value="PENDING">Pending</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Entity List */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : filteredEntities.length === 0 ? (
                <div className="bg-surface-dark border border-slate-800 rounded-xl p-12 text-center">
                    <p className="text-slate-400">No entities found</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredEntities.map((entity) => (


                        <div
                            key={entity.id}
                            onClick={() => navigate(`/projects/${currentProject?.value}/entities/${entity.entity_id}`)}
                            className="bg-surface-dark border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition-colors cursor-pointer"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="text-lg font-semibold text-white">
                                            {entity.entity_id}
                                        </h3>
                                        <EntityBadge
                                            status={entity.spec_status}
                                            blockedReason={entity.spec_blocked_reason}
                                            confidence={entity.precheck_confidence}
                                        />
                                    </div>

                                    {entity.title && (
                                        <p className="text-slate-400 mb-3">{entity.title}</p>
                                    )}

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <div>
                                            <span className="text-slate-500">Type:</span>
                                            <span className="ml-2 text-white">{entity.entity_type}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Chunks:</span>
                                            <span className="ml-2 text-white">
                                                {entity.precheck_chunk_count || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Sources:</span>
                                            <span className="ml-2 text-white">
                                                {entity.precheck_sources_count || 'N/A'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Confidence:</span>
                                            <span className="ml-2 text-white">
                                                {entity.precheck_confidence
                                                    ? `${Math.round(entity.precheck_confidence * 100)}%`
                                                    : 'N/A'}
                                            </span>
                                        </div>
                                    </div>

                                    {entity.spec_blocked_reason && (
                                        <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                                            <p className="text-sm text-yellow-400">
                                                <strong>Blocked:</strong> {entity.spec_blocked_reason.replace(/_/g, ' ')}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="ml-4">
                                    {(entity.spec_status === 'BLOCKED' || entity.spec_status === 'FAILED') && (
                                        <button
                                            onClick={() => handleRetry(entity.entity_id)}
                                            disabled={retryingId === entity.entity_id}
                                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                                        >
                                            <RefreshCw
                                                className={`w-4 h-4 ${retryingId === entity.entity_id ? 'animate-spin' : ''}`}
                                            />
                                            Retry
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Summary */}
            <div className="bg-surface-dark border border-slate-800 rounded-xl p-4">
                <p className="text-sm text-slate-400">
                    Showing {filteredEntities.length} of {entities.length} entities
                    {statusFilter !== 'all' && ` (filtered by ${statusFilter})`}
                </p>
            </div>
        </div>
    );
};

export default Entities;
